/**
 * Donor Service
 *
 * Singleton that checks and caches donor status from Supabase `user_profiles` table.
 * The cache is persisted to localStorage so it survives page reloads without
 * requiring a network call on every visit.
 *
 * ⛔ `isDonor()` HAS NO PRODUCTION CONSUMERS, AND THAT IS DELIBERATE (WR-468,
 * CEO dispatch, founder-ratified 2026-09-06). This docblock used to say it was
 * "used by the UI to gate donor-only features (project export, full backup)" and
 * that "Menu items call isDonor() synchronously to show/hide donor features".
 * Both were true until 2026-09-06 and are now false: backup, export, import and
 * full restore are free for every signed-in user, and the single call site at
 * Nav.tsx was removed.
 *
 * The service is KEPT because pricing is parked at the CEO seat, the donor field
 * is live in Supabase, and the admin surfaces read it. Its one remaining
 * production consumer is supabaseAuthService, which maintains the cache.
 *
 * ⛔ A NEW CALLER OF isDonor() THAT HIDES OR BLOCKS A FEATURE IS A GATE, and a
 * gate is what WR-468 removed. Reading donor status to DISPLAY something (a
 * badge, a perk list, a credits roll) is fine. Gated at
 * src/__tests__/architecture/donorGateRemoved.test.ts.
 *
 * Lifecycle:
 * - On sign-in: SupabaseAuthService calls `checkAndCacheDonorStatus(userId, email)`
 * - On sign-out: SupabaseAuthService calls `clearCache()`
 * - On page load: constructor reads cache from localStorage (no network call)
 *
 * Note: This service does NOT import supabaseAuthService to avoid circular dependencies.
 * Instead, the auth service passes user info as parameters when calling donor methods.
 */

import { supabase, isSupabaseConfigured } from "../supabase/client";
import { debugLog, debugWarn, debugError } from "../utils/debug";

interface DonorStatusCache {
  isDonor: boolean;
  userId: string;
  checkedAt: string; // ISO date
}

const DONOR_CACHE_KEY = "stagecraft_donor_status";
export const DONOR_REFRESH_SIGNAL_KEY = "stagecraft_donor_refresh";

class DonorService {
  private static instance: DonorService;

  private cachedStatus: DonorStatusCache | null = null;

  private constructor() {
    this.loadCacheFromStorage();

    // Listen for cross-tab donor refresh signals (e.g., from Thank You page after donation)
    if (typeof window !== "undefined") {
      window.addEventListener("storage", (event) => {
        if (event.key === DONOR_REFRESH_SIGNAL_KEY) {
          debugLog("DonorService: Cross-tab donor refresh signal received");
          this.refreshDonorStatus();
        }
      });
    }
  }

  public static getInstance(): DonorService {
    if (!DonorService.instance) {
      DonorService.instance = new DonorService();
    }
    return DonorService.instance;
  }

  /**
   * Synchronous check — returns cached donor status.
   * Returns false if no cache exists or Supabase is not configured.
   * Does NOT make network calls.
   *
   * Optionally pass currentUserId to validate cache belongs to the right user.
   */
  isDonor(currentUserId?: string): boolean {
    // In local dev (no auth), grant donor access for testing
    if (import.meta.env.VITE_FORCE_AUTH === "false") return true;

    if (!this.cachedStatus) return false;

    // If a userId is provided, validate the cache belongs to this user
    if (currentUserId && this.cachedStatus.userId !== currentUserId) {
      return false;
    }

    return this.cachedStatus.isDonor;
  }

  /**
   * Query Supabase for the user's donor status and cache the result.
   * Called by SupabaseAuthService on SIGNED_IN. If the user has no profile row,
   * one is created automatically via ensureUserProfile().
   *
   * @param userId - The authenticated user's ID
   * @param email - The authenticated user's email (for profile creation)
   * @returns The donor status (true/false)
   */
  async checkAndCacheDonorStatus(
    userId: string,
    email: string,
  ): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) {
      debugWarn(
        "DonorService: Supabase not configured, cannot check donor status",
      );
      return false;
    }

    if (!userId) {
      debugWarn("DonorService: No userId provided, cannot check donor status");
      return false;
    }

    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("donor_status")
        .eq("user_id", userId)
        .single();

      if (error) {
        // PGRST116 = no rows returned — user has no profile yet
        if (error.code === "PGRST116") {
          debugLog(
            "DonorService: No profile found, creating one for user:",
            userId,
          );
          await this.ensureUserProfile(userId, email);
          // New profiles default to donor_status = false
          this.updateCache(false, userId);
          return false;
        }

        debugError("DonorService: Error checking donor status:", error.message);
        return false;
      }

      const isDonor = data?.donor_status === true;
      this.updateCache(isDonor, userId);
      debugLog(
        "DonorService: Donor status cached:",
        isDonor,
        "for user:",
        userId,
      );
      return isDonor;
    } catch (err) {
      debugError("DonorService: Unexpected error checking donor status:", err);
      return false;
    }
  }

  /**
   * Re-query Supabase for the current user's donor status and update the cache.
   * Called after the Thank You page confirms the webhook has processed,
   * or via cross-tab storage event signal.
   *
   * @returns The refreshed donor status, or false if refresh failed
   */
  async refreshDonorStatus(): Promise<boolean> {
    const userId = this.cachedStatus?.userId;
    if (!userId) return false;
    if (!isSupabaseConfigured() || !supabase) return false;

    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("donor_status")
        .eq("user_id", userId)
        .single();

      if (error) {
        debugError(
          "DonorService: Error refreshing donor status:",
          error.message,
        );
        return false;
      }

      const isDonor = data?.donor_status === true;
      this.updateCache(isDonor, userId);
      debugLog("DonorService: Donor status refreshed:", isDonor);
      return isDonor;
    } catch (err) {
      debugError(
        "DonorService: Unexpected error refreshing donor status:",
        err,
      );
      return false;
    }
  }

  /**
   * Ensure a user_profiles row exists for the given user.
   * Inserts a new row with default values if none exists.
   * This is the auto-create-on-first-signin logic.
   *
   * @param userId - The authenticated user's ID
   * @param email - The authenticated user's email
   */
  async ensureUserProfile(userId: string, email: string): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) return;
    if (!userId) return;

    try {
      // Check if profile already exists
      const { data: existing } = await supabase
        .from("user_profiles")
        .select("user_id")
        .eq("user_id", userId)
        .single();

      if (existing) {
        debugLog("DonorService: Profile already exists for user:", userId);
        return;
      }

      // Insert new profile with defaults
      const { error } = await supabase.from("user_profiles").insert({
        user_id: userId,
        email: email || "",
        donor_status: false,
        total_donated: 0,
        tshirt_eligible: false,
      });

      if (error) {
        // Unique constraint violation means profile was created by another tab/process
        if (error.code === "23505") {
          debugLog(
            "DonorService: Profile already exists (race condition), continuing",
          );
          return;
        }
        debugError("DonorService: Error creating user profile:", error.message);
      } else {
        debugLog("DonorService: Created profile for user:", userId);
      }
    } catch (err) {
      debugError("DonorService: Unexpected error in ensureUserProfile:", err);
    }
  }

  /**
   * Clear the cached donor status. Called on sign-out.
   */
  clearCache(): void {
    this.cachedStatus = null;
    try {
      localStorage.removeItem(DONOR_CACHE_KEY);
    } catch {
      // localStorage may be unavailable in some contexts
    }
    debugLog("DonorService: Cache cleared");
  }

  private updateCache(isDonor: boolean, userId: string): void {
    const status: DonorStatusCache = {
      isDonor,
      userId,
      checkedAt: new Date().toISOString(),
    };
    this.cachedStatus = status;
    this.saveCacheToStorage(status);
  }

  private loadCacheFromStorage(): void {
    try {
      const raw = localStorage.getItem(DONOR_CACHE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      // Validate shape
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        typeof parsed.isDonor === "boolean" &&
        typeof parsed.userId === "string" &&
        typeof parsed.checkedAt === "string"
      ) {
        this.cachedStatus = parsed as DonorStatusCache;
      } else {
        // Invalid cache shape — remove it
        localStorage.removeItem(DONOR_CACHE_KEY);
      }
    } catch {
      // JSON parse error or localStorage unavailable
      this.cachedStatus = null;
    }
  }

  private saveCacheToStorage(status: DonorStatusCache): void {
    try {
      localStorage.setItem(DONOR_CACHE_KEY, JSON.stringify(status));
    } catch {
      // localStorage may be full or unavailable
      debugWarn("DonorService: Failed to save cache to localStorage");
    }
  }
}

export const donorService = DonorService.getInstance();
